/**
 * scrollable-sticky-header.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import useScrollPosition from 'utils/use-scroll-position';
import { shouldRenderMobileLayout } from 'utils/app-context';


const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    display: 'flex',
    overflow: 'hidden',
  },
  scrollContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: 1,
    scrollBehavior: 'smooth',
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 17,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    pointerEvents: 'none',
    opacity: 0,
    transform: 'translateY(100%)',
    transition: '100ms ease-out',
    boxShadow: theme.shadows[3],
    '&.show': {
      pointerEvents: 'auto',
      opacity: 1,
      transform: 'translateY(0%)',
    },
    [theme.breakpoints.down('md')]: {
      right: 0,
    },
    '& .scrollable-tabs': {
      marginLeft: -40
    },
    '& .desktop-drawer-header-container': {
      padding: `${theme.spacing(1)} ${theme.spacing(4)}`,
      '& .title': {
        fontSize: theme.typography.sizes[4],
      }
    }
  }
}));


// children is expected to be a function that takes a single argument
// which will be the element that triggers the header to show.
// this function should also return the full scrollable contents.
const ScrollableStickyHeader = (props) => {
  const { 
    headerContent,
    children,
    triggerOffset,
  } = props;
  const classes = useStyles();
  const scrollRef = React.useRef();
  const triggerRef = React.useRef();
  const [scrollTop, setScrollTop] = React.useState(0);
  useScrollPosition(
    ({ currPos }) => {
      setScrollTop(currPos.y);
    }, 
    [], 
    scrollRef,
  );
  const triggerPoint = triggerRef.current
    ? triggerRef.current.offsetTop + (triggerOffset !== undefined ? triggerOffset : -20)
    : 0;


  const newChildren = React.useMemo(() => (
    children(<div ref={triggerRef} />)
  ), [children, triggerRef]);


  const showHeader = React.useMemo(() => (
    scrollTop > 0 && scrollTop > triggerPoint
  ), [scrollTop, triggerPoint]);


  return (
    <div className={classes.container}>

      {/* scroll container */}
      <div 
        ref={scrollRef}
        className={classes.scrollContainer}
      >
        {newChildren}
      </div>
      
      {/* sticky header */}
      {headerContent &&
      <div className={`${classes.headerContainer} ${showHeader ? 'show' : ''}`}>
        {headerContent}
      </div>}
      
    </div>
  );
}


// wrapper to fully disable functionality when unused
export default function ScrollableStickyHeaderWrapper(props) {
  if (!props.headerContent || (props.disableForMobile && shouldRenderMobileLayout())) {
    return <>{props.children()}</>;
  } else {
    return <ScrollableStickyHeader {...props} />;
  }
}